import React from 'react';
import classNames from 'classnames';
import { HiChevronUp, HiChevronDown } from 'react-icons/hi';

export interface TableColumn<T = any> {
  key: string;
  title: React.ReactNode;
  dataIndex?: string;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  sortable?: boolean;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export interface TableProps<T = any> {
  columns: TableColumn<T>[];
  data: T[];
  loading?: boolean;
  rowKey?: string | ((record: T) => string);
  onRow?: (record: T, index: number) => React.HTMLAttributes<HTMLTableRowElement>;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  bordered?: boolean;
  striped?: boolean;
  hoverable?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (key: string, order: 'asc' | 'desc') => void;
  emptyText?: string;
  renderExpandedRow?: (record: T, index: number) => React.ReactNode;
}

const Table = <T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  rowKey = 'id',
  onRow,
  className,
  size = 'md',
  bordered = true,
  striped = true,
  hoverable = true,
  sortBy,
  sortOrder,
  onSort,
  emptyText = 'No data available',
  renderExpandedRow,
}: TableProps<T>) => {
  // Add null safety for data
  const safeData = data || [];
  
  const getRowKey = (record: T, index: number): string => {
    if (typeof rowKey === 'function') {
      return rowKey(record);
    }
    return record[rowKey] || index.toString();
  };

  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable || !onSort) return;
    
    const key = column.dataIndex || column.key;
    let newOrder: 'asc' | 'desc' = 'asc';
    
    if (sortBy === key && sortOrder === 'asc') {
      newOrder = 'desc';
    }
    
    onSort(key, newOrder);
  };

  const renderCell = (column: TableColumn<T>, record: T, index: number) => {
    if (column.render) {
      return column.render(record[column.dataIndex || column.key], record, index);
    }
    return record[column.dataIndex || column.key];
  };

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const paddingClasses = {
    sm: 'px-3 py-2',
    md: 'px-4 py-3',
    lg: 'px-6 py-4',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className={classNames('overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className={classNames('min-w-full divide-y divide-gray-200', sizeClasses[size])}>
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={classNames(
                    'font-medium text-gray-900 tracking-wider',
                    paddingClasses[size],
                    {
                      'text-left': column.align === 'left' || !column.align,
                      'text-center': column.align === 'center',
                      'text-right': column.align === 'right',
                      'cursor-pointer hover:bg-gray-100': column.sortable,
                      'border-l border-gray-200': bordered,
                    },
                    column.className
                  )}
                  style={{ width: column.width }}
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center">
                    <span>{column.title}</span>
                    {column.sortable && (
                      <div className="ml-2 flex flex-col">
                        <HiChevronUp
                          className={classNames('h-3 w-3', {
                            'text-primary-600': sortBy === (column.dataIndex || column.key) && sortOrder === 'asc',
                            'text-gray-400': !(sortBy === (column.dataIndex || column.key) && sortOrder === 'asc'),
                          })}
                        />
                        <HiChevronDown
                          className={classNames('h-3 w-3 -mt-1', {
                            'text-primary-600': sortBy === (column.dataIndex || column.key) && sortOrder === 'desc',
                            'text-gray-400': !(sortBy === (column.dataIndex || column.key) && sortOrder === 'desc'),
                          })}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={classNames('bg-white divide-y divide-gray-200', {
            'divide-gray-200': striped,
          })}>
            {safeData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className={classNames('text-center text-gray-500', paddingClasses[size])}
                >
                  {emptyText}
                </td>
              </tr>
            ) : (
              safeData.map((record, index) => {
                const expandedContent = renderExpandedRow ? renderExpandedRow(record, index) : null;
                return (
                  <React.Fragment key={getRowKey(record, index)}>
                    <tr
                      className={classNames({
                        'bg-gray-50': striped && index % 2 === 1,
                        'hover:bg-gray-100': hoverable,
                      })}
                      {...(onRow ? onRow(record, index) : {})}
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={classNames(
                            'text-gray-900',
                            paddingClasses[size],
                            {
                              'text-left': column.align === 'left' || !column.align,
                              'text-center': column.align === 'center',
                              'text-right': column.align === 'right',
                              'border-l border-gray-200': bordered,
                            },
                            column.className
                          )}
                          style={{ width: column.width }}
                        >
                          {renderCell(column, record, index)}
                        </td>
                      ))}
                    </tr>
                    {expandedContent && (
                      <tr className="bg-white">
                        {expandedContent}
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
